import { get } from 'lodash';
import React, { FunctionComponent, useCallback, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { IFileInputProps } from './types';
import { saveFormFile, clearFormFile } from '../../../../../app/page/page-storage/users/user-form/user-form.actions';

export const FileInput: FunctionComponent<IFileInputProps> = ({
  id,
  name,
  defaultValue,
  validation,
  disabled,
  label,
  maxFileSize = 10,
  acceptableFormats,
  accept = 'image/*',
  showPreview = true,
  helpText,
  storeInUserForm = true,
}) => {
  const [preview, setPreview] = useState<string | null>(defaultValue || null);
  const [fileName, setFileName] = useState<string>('');
  const { control, errors, setError, clearErrors } = useFormContext();
  const dispatch = useDispatch();

  // Calculate display values for file size
  const fileSizeValue = maxFileSize < 1 ? maxFileSize * 1024 : maxFileSize;
  const fileSizeUnit = maxFileSize < 1 ? 'KB' : 'MB';

  const onFileInput = useCallback(
    (event, onChange) => {
      if (event && event.currentTarget && event.currentTarget.files && event.currentTarget.files[0]) {
        const fileBlob = event.currentTarget.files[0];
        const filesize = (fileBlob.size / 1024 / 1024).toFixed(4);

        if (+filesize <= maxFileSize) {
          setFileName(fileBlob.name);

          // Show preview for image files
          if (fileBlob.type.startsWith('image/') && showPreview) {
            setPreview(URL.createObjectURL(fileBlob));
          }

          const reader = new FileReader();
          reader.readAsDataURL(fileBlob);
          reader.onloadend = () => {
            onChange(reader.result);
            if (storeInUserForm) {
              dispatch(saveFormFile(reader.result as string));
            }
          };
          clearErrors(name);
        } else {
          setError(name, {
            type: 'maxLength',
            message: `File should be smaller than ${fileSizeValue}${fileSizeUnit}`,
          });
        }
      }
    },
    [clearErrors, dispatch, fileSizeUnit, fileSizeValue, maxFileSize, name, setError, showPreview, storeInUserForm],
  );

  const handleClearFile = useCallback(
    (onChange) => {
      setPreview(null);
      setFileName('');
      onChange('');
      clearErrors(name);
      if (storeInUserForm) {
        dispatch(clearFormFile());
      }
    },
    [clearErrors, dispatch, name, storeInUserForm],
  );

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue ? defaultValue : ''}
      rules={validation}
      render={(controllerProps) => (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
          <input
            type="file"
            id={id}
            name={name}
            style={{ display: 'none' }}
            onChange={(event) => onFileInput(event, controllerProps.onChange)}
            accept={accept}
            disabled={disabled}
          />

          {/* Preview Section - Left Side - Clickable */}
          {showPreview && (
            <label
              htmlFor={id}
              className="new-account-image"
              style={{
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
              }}
            >
              {preview ? (
                <img
                  alt="File preview"
                  src={preview}
                  style={{
                    width: '190px',
                    height: '100px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    transition: 'opacity 0.2s',
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.opacity = '0.8')}
                  onMouseOut={(e) => (e.currentTarget.style.opacity = '1')}
                />
              ) : (
                <div
                  style={{
                    width: '190px',
                    height: '100px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    backgroundColor: '#f5f5f5',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#e8e8e8')}
                  onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                >
                  <i className="icon-image" style={{ fontSize: '32px', color: '#999' }}></i>
                </div>
              )}
            </label>
          )}

          {/* Input Section - Right Side */}
          <div style={{ flex: 1 }}>
            <div className="new-account-desc" style={{ paddingLeft: showPreview ? '0' : '25px' }}>
              {!!get(errors, name, false) && (
                <label htmlFor={name} style={{ color: 'red' }}>
                  {get(errors, `${name}.message`)}
                </label>
              )}
              <p className="bold">{label}</p>
              {fileName && (
                <p className="small-text" style={{ marginBottom: '8px' }}>
                  Selected: {fileName}
                </p>
              )}
              <p className="small-text">
                {`Acceptable formats: ${
                  acceptableFormats ? acceptableFormats : 'All'
                }. Max size: ${fileSizeValue}${fileSizeUnit}.`}
              </p>
              {helpText && <p className="small-text">{helpText}</p>}
              {(preview || fileName) && (
                <div style={{ marginTop: '12px' }}>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleClearFile(controllerProps.onChange)}
                    disabled={disabled}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    />
  );
};
